/**
 * StudyGroupSyncOrchestrator — bridges the `sync.groupMemberships`
 * collection (personal per-user list, lives on UserDo) with the pool
 * of active group WebSockets (one per group).
 *
 * Responsibilities:
 *   1. Subscribe to memberships via Zustand.
 *   2. Acquire one pool ref per non-deleted membership — this keeps
 *      the pool entry alive so the `<StudyGroupSubscription>` below
 *      stays mounted and its `useStudyGroupDoSync` keeps the socket
 *      open.
 *   3. Render a stable list of subscription components that each
 *      mount the sync hook for one groupId.
 *
 * Everything flows from the single Zustand membership list, so joining
 * a group anywhere (invite accept, admin add-member) automatically
 * spawns the socket once the server has broadcast the new membership
 * onto UserDo.
 */

import type { SyncGroupMembership } from '@flashka/shared/sync-engine/schema/entities';

import React, { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useEnsureSGMirrorsForGroup } from '../study-groups/use-seed-sg-mirror';
import type { SGLegacyMigrationClient } from './engine/study-group-do-adapter';
import { poolAcquire, poolRelease, useActiveGroupIds } from './group-connection-pool';
import { useStudyGroupDoSync } from './hook/use-study-group-do-sync';
import { useFlashkaStore } from './store/flashka-store';

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

export function StudyGroupSyncOrchestrator({
  getToken,
  userId,
  sgLegacyClient,
}: {
  getToken?: () => Promise<string | null>;
  /** Signed-in user — threaded to the SG adapter's legacy-notes stash. */
  userId?: string | null;
  /** Platform SG legacy-notes stash (web Dexie / native AsyncStorage). */
  sgLegacyClient?: SGLegacyMigrationClient;
}): React.ReactElement | null {
  const memberships = useFlashkaStore(
    useShallow((s) => {
      const all = (s as unknown as { groupMemberships?: SyncGroupMembership[] })
        .groupMemberships;
      return Array.isArray(all) ? all.filter((m) => !m.deletedTimeISO) : [];
    }),
  );

  // Reconcile pool refs against the live membership list. On each
  // render we compute the set of groupIds we should be holding and
  // adjust by diffing the previous set.
  useEffect(() => {
    const wanted = new Set(memberships.map((m) => m.groupId));
    for (const id of wanted) poolAcquire(id);
    return () => {
      for (const id of wanted) poolRelease(id);
    };
  }, [memberships]);

  // Render one subscription per currently-pooled group. `useActiveGroupIds`
  // gives us the pool's view (which includes groups still in their
  // 30s unmount grace period), so briefly navigating away doesn't
  // flap the WebSocket.
  const activeIds = useActiveGroupIds();

  return (
    <>
      {activeIds.map((groupId) => (
        <StudyGroupSubscription
          key={groupId}
          groupId={groupId}
          getToken={getToken}
          userId={userId}
          sgLegacyClient={sgLegacyClient}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-group subscription host — exactly one `useStudyGroupDoSync` call
// per groupId. Isolated as its own component so React's hooks-rules
// tolerate a dynamic list.
// ---------------------------------------------------------------------------

function StudyGroupSubscription({
  groupId,
  getToken,
  userId,
  sgLegacyClient,
}: {
  groupId: string;
  getToken?: () => Promise<string | null>;
  userId?: string | null;
  sgLegacyClient?: SGLegacyMigrationClient;
}): null {
  useStudyGroupDoSync(groupId, getToken, { userId, sgLegacyClient });
  // Keep each member's per-deck mirror IDENTITY current for this group as
  // soon as the socket is open, and reactively as the SG deck list changes.
  // This is what lets a LIVE deck delete→re-add re-create the member's mirror
  // WITHOUT a reload. Previously web only seeded on the deck/folder view, so
  // a re-add while the member was elsewhere left them with no linked mirror —
  // no source tab and no live card sync — until a full reload re-ran
  // sync-init.
  //
  // Identity ONLY. The card merge into existing mirrors happens on the
  // surfaces that render them (mobile group deck list, web SG deck route);
  // doing it here re-merged every deck of every group on every group card
  // frame, at app start, while Home was painting. See
  // `useEnsureSGMirrorsForGroup`.
  useEnsureSGMirrorsForGroup(groupId);
  return null;
}
